import { ReactNode } from 'react';
import currency from 'currency.js';
import { Tooltip, Skeleton as AntdSkeleton } from 'antd';

import { ErrorWarning, PriceTag3 } from '@styled-icons/remix-line';

import { percent, UsdFormatter } from 'v3/utils/formatNumber';
import theme from 'styles/theme';

import IconItem from '../IconItem';
import Range, { monthsArray } from '../Range';
import Tag from '../Tag';

import * as S from './styles';

export type OfferBoxProps = {
  monthlyPayment: number;
  halfPayment: number;
  weeklyHalfPayment?: number;
  amount: number;
  eapr: number;
  term: number;
  paymentOption: 'full' | 'half';
  downPayment?: number;
  range: [number, number];
  referralCode?: string;
  referralCodeSavings?: number;
  tooltip?: string;
  children?: ReactNode;
  loading?: boolean;
  /** 'grid' puts the stats in columns, with a small-caps label above the value. The
   *  default 'rows' keeps the label-left / value-right layout used by
   *  ReviewOfferWaitlist and the v2 screens. */
  statsLayout?: 'rows' | 'grid';
  /** Opt out of the down payment stat. Defaults on, so existing consumers keep it. */
  showDownPayment?: boolean;
  weeklyCycle: boolean;
  weekTerm: number;
  weeklyPayment: number;
  firstPayment?: number;
};

const PAYMENT_OPTIONS = {
  full: 'Full payment',
  half: 'Half payment',
};

const Skeleton = () => {
  return (
    <S.SkeletonContainer aria-label="Loading data">
      <AntdSkeleton.Input active key="skeleton-0" />
      {Array.from({ length: 6 }).map((_, index) => (
        <AntdSkeleton.Input
          active
          key={`skeleton-${index + 1}`}
          style={{ width: '100%' }}
        />
      ))}
    </S.SkeletonContainer>
  );
};

const OfferBox = ({
  amount,
  eapr,
  monthlyPayment,
  weeklyPayment,
  weekTerm,
  halfPayment,
  weeklyHalfPayment,
  paymentOption,
  range,
  term,
  downPayment,
  referralCode,
  tooltip,
  children,
  referralCodeSavings = 0,
  loading = false,
  weeklyCycle = false,
  firstPayment,
  statsLayout = 'rows',
  showDownPayment = true,
}: OfferBoxProps) => {
  const [initialMonth, finalMonth] = range;
  const hasFirstPaymentDiscount = !!firstPayment;
  const showAsRow = paymentOption === 'half' || hasFirstPaymentDiscount;

  return (
    <S.Wrapper>
      {loading ? (
        <Skeleton />
      ) : (
        <>
          <S.Content>
            <S.Row border displayRow={showAsRow}>
              <S.MonthlyPaymentBox displayRow={showAsRow}>
                {/* 1st slot: first-payment discount (if present), otherwise half payment (if present), otherwise regular payment */}
                <S.ChosenValue>
                  <S.Label
                    color={
                      !hasFirstPaymentDiscount && paymentOption === 'half'
                        ? 'neutralSilver50'
                        : undefined
                    }
                  >
                    <>
                      Loan amount{' '}
                      <strong aria-label="Loan amount">
                        {currency(amount, { precision: 0 }).format()}
                      </strong>
                      {hasFirstPaymentDiscount ? (
                        <><br /><span>First payment</span></>
                      ) : paymentOption === 'half' ? (
                        <><br /><span>Starting at</span></>
                      ) : null}
                    </>
                  </S.Label>
                  {hasFirstPaymentDiscount ? (
                    weeklyCycle ? (
                      <S.WeeklyPayment aria-label="First Payment value">
                        {currency(firstPayment).format()}
                      </S.WeeklyPayment>
                    ) : (
                      <S.MonthlyPayment aria-label="First Payment value">
                        {currency(firstPayment).format()}
                      </S.MonthlyPayment>
                    )
                  ) : weeklyCycle ? (
                    <S.WeeklyPayment aria-label="Weekly Payment value">
                      {currency(
                        paymentOption === 'half' && weeklyHalfPayment != null
                          ? weeklyHalfPayment
                          : weeklyPayment
                      ).format()}
                    </S.WeeklyPayment>
                  ) : (
                    <S.MonthlyPayment aria-label="Monthly Payment value">
                      {currency(
                        paymentOption === 'half' ? halfPayment : monthlyPayment
                      ).format()}
                    </S.MonthlyPayment>
                  )}
                </S.ChosenValue>

                {/* 2nd slot: half payment — only shown when there is also a first-payment discount */}
                {hasFirstPaymentDiscount && paymentOption === 'half' && (
                  <S.OriginalValueBox>
                    <S.Label>
                      <span>Starting {weeklyCycle ? 'week' : 'month'} 2</span>
                    </S.Label>
                    {weeklyCycle ? (
                      <S.WeeklyPayment aria-label="Weekly half payment value">
                        {currency(
                          weeklyHalfPayment != null
                            ? weeklyHalfPayment
                            : weeklyPayment
                        ).format()}
                      </S.WeeklyPayment>
                    ) : (
                      <S.MonthlyPayment aria-label="Monthly half payment value">
                        {currency(halfPayment || monthlyPayment).format()}
                      </S.MonthlyPayment>
                    )}
                  </S.OriginalValueBox>
                )}

                {/* 3rd slot: regular payment — shown whenever something precedes it */}
                {(hasFirstPaymentDiscount || paymentOption === 'half') && (
                  <S.OriginalValueBox>
                    <S.Label>
                      <span>
                        {paymentOption === 'half'
                          ? 'After Credit Voucher'
                          : `Starting ${weeklyCycle ? 'week' : 'month'} 2`}
                      </span>
                    </S.Label>
                    {weeklyCycle ? (
                      <S.WeeklyPayment aria-label="Regular weekly payment value">
                        {currency(weeklyPayment).format()}
                      </S.WeeklyPayment>
                    ) : (
                      <S.MonthlyPayment aria-label="Regular monthly payment value">
                        {currency(monthlyPayment).format()}
                      </S.MonthlyPayment>
                    )}
                  </S.OriginalValueBox>
                )}
              </S.MonthlyPaymentBox>

              <S.TagContainer displayRow={showAsRow}>
                <Tag variant="secondary">
                  {PAYMENT_OPTIONS[paymentOption]} selected
                </Tag>
              </S.TagContainer>
            </S.Row>
            {(() => {
              const stats = (
                <>
                  <S.Row>
                    <S.Text>EAPR (Included)</S.Text>
                    <S.Text color="neutralSilver80" aria-label="EAPR value">
                      {parseFloat((eapr ?? 0).toFixed(4))}%
                    </S.Text>
                  </S.Row>
                  <S.Row>
                    <S.Text>Loan term</S.Text>
                    <S.Text color="neutralSilver80" aria-label="Loan term">
                      {weeklyCycle ? `${weekTerm} weeks` : `${term} months`}
                    </S.Text>
                  </S.Row>
                  {showDownPayment && (
                    <S.Row border={statsLayout !== 'grid'}>
                      <S.RowBorderWrapper>
                        <S.Text>Down Payment</S.Text>
                        <S.Text
                          color="neutralSilver80"
                          aria-label="Down payment value"
                        >
                          {downPayment
                            ? currency(downPayment, { precision: 0 }).format()
                            : '0'}
                        </S.Text>
                      </S.RowBorderWrapper>
                    </S.Row>
                  )}
                </>
              );

              // Without the wrapper, 'rows' markup is identical to before, so
              // SignDeal, ReviewOfferWaitlist and the v2 screens are unchanged.
              return statsLayout === 'grid' ? (
                <S.StatsGrid>{stats}</S.StatsGrid>
              ) : (
                stats
              );
            })()}
            {referralCode && (
              <S.Row>
                <S.RowBorderWrapper>
                  <IconItem
                    icon={
                      <PriceTag3
                        size={18}
                        color={theme.colors.statusSuccess30}
                        style={{ transform: 'scale(-1, 1)' }}
                      />
                    }
                    text={
                      <S.Text aria-label="Referral code">{referralCode}</S.Text>
                    }
                  />

                  {referralCodeSavings > 0 && (
                    <S.Text>
                      <span aria-label="Referral Code savings">
                        - {UsdFormatter.format(referralCodeSavings)}
                      </span>
                    </S.Text>
                  )}
                </S.RowBorderWrapper>
              </S.Row>
            )}
          </S.Content>

          {/* {tooltip ? (
            <Tooltip title={tooltip}>
              <S.MonthsRange>
                <S.Tooltip>
                  <S.Text>
                    Months till you get the car <strong>by 80%</strong>
                  </S.Text>

                  <ErrorWarning
                    size={20}
                    color={theme.colors.neutralSilver60}
                    aria-label="Tooltip icon"
                  />
                </S.Tooltip>
                <Range
                  initialValue={initialMonth}
                  finalValue={finalMonth}
                  months={term as keyof typeof monthsArray}
                />
              </S.MonthsRange>
            </Tooltip>
          ) : (
            <S.MonthsRange>
              <S.Tooltip>
                <S.Text>
                  Months till you get the car <strong>by 80%</strong>
                </S.Text>
              </S.Tooltip>
              <Range
                initialValue={initialMonth}
                finalValue={finalMonth}
                months={term as keyof typeof monthsArray}
              />
            </S.MonthsRange>
          )} */}
        </>
      )}
      {children}
    </S.Wrapper>
  );
};

export default OfferBox;
